// Roles & Permissions edit. Same look/feel as AdminUserEdit:
// bordered Paper card, MUI standard inputs, Bootstrap-blue Save +
// grey Reset. System roles have their Name field disabled (the seed
// names are joined by AdminUserList → keep stable).

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";
import { apiFetch } from "@/lib/api";
import { useToast } from "@/components/Toast";

interface AdminRole {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  can_manage_admins: boolean;
  can_manage_app_users: boolean;
  can_manage_jobs: boolean;
  can_send_push: boolean;
  can_view_reports: boolean;
}

interface FormState {
  name: string;
  description: string;
  can_manage_admins: boolean;
  can_manage_app_users: boolean;
  can_manage_jobs: boolean;
  can_send_push: boolean;
  can_view_reports: boolean;
}

const PERM_FIELDS: Array<{ key: keyof FormState; label: string }> = [
  { key: "can_manage_admins", label: "Can manage admins" },
  { key: "can_manage_app_users", label: "Can manage app users" },
  { key: "can_manage_jobs", label: "Can manage jobs" },
  { key: "can_send_push", label: "Can send notifications" },
  { key: "can_view_reports", label: "Can view reports" },
];

export function RoleEdit() {
  const { id = "" } = useParams<{ id: string }>();
  const { show, ToastEl } = useToast();

  const detail = useQuery<AdminRole>({
    queryKey: ["admin-roles", id],
    queryFn: () => apiFetch<AdminRole>(`/v1/admin/roles/${id}`),
    enabled: Boolean(id),
  });

  const initial = (r: AdminRole): FormState => ({
    name: r.name,
    description: r.description ?? "",
    can_manage_admins: r.can_manage_admins,
    can_manage_app_users: r.can_manage_app_users,
    can_manage_jobs: r.can_manage_jobs,
    can_send_push: r.can_send_push,
    can_view_reports: r.can_view_reports,
  });

  const [form, setForm] = useState<FormState | null>(null);
  useEffect(() => {
    if (detail.data) setForm(initial(detail.data));
  }, [detail.data]);

  const original = useMemo(
    () => (detail.data ? initial(detail.data) : null),
    [detail.data],
  );

  const patch = useMutation({
    mutationFn: async () => {
      if (!form || !original) return;
      const body: Record<string, unknown> = {};
      if (!detail.data?.is_system && form.name !== original.name) {
        body.name = form.name;
      }
      const desc = form.description.trim();
      const origDesc = original.description.trim();
      if (desc !== origDesc) body.description = desc.length ? desc : null;
      for (const { key } of PERM_FIELDS) {
        if (form[key] !== original[key]) body[key] = form[key];
      }
      if (Object.keys(body).length === 0) {
        show("No changes to save", "info");
        return;
      }
      await apiFetch(`/v1/admin/roles/${id}`, { method: "PATCH", body });
    },
    onSuccess: () => {
      show("Role updated", "success");
      detail.refetch();
    },
    onError: (err) => show(err instanceof Error ? err.message : "Update failed", "error"),
  });

  if (detail.isLoading || !form) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load role</Typography>
      </Box>
    );
  }

  const isSystem = detail.data.is_system;

  // Same Bootstrap palette + bordered-Paper card as AdminUserEdit.
  const cardSx = {
    border: `1px solid ${tokens.colors.admin.border}`,
    borderRadius: 1,
    p: 3,
  };
  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const primaryBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Paper elevation={0} sx={cardSx}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              variant="standard"
              fullWidth
              required
              label="Role Name"
              inputProps={{ maxLength: 80 }}
              disabled={isSystem}
              helperText={isSystem ? "System role — name locked" : " "}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <Box sx={{ gridColumn: { xs: "auto", md: "span 3" } }}>
              <TextField
                variant="standard"
                fullWidth
                label="Description"
                inputProps={{ maxLength: 500 }}
                InputLabelProps={{ shrink: true }}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: tokens.font.sizes.body,
              fontWeight: tokens.font.weights.semibold,
              color: tokens.colors.admin.textHeading,
              mb: 1,
            }}
          >
            Permissions
          </Typography>
          <Stack spacing={1} sx={{ mb: 3 }}>
            {PERM_FIELDS.map(({ key, label }) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={Boolean(form[key])}
                    onChange={(e) =>
                      setForm({ ...form, [key]: e.target.checked })
                    }
                  />
                }
                label={label}
              />
            ))}
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              disabled={patch.isPending}
              sx={primaryBtn}
              onClick={() => patch.mutate()}
            >
              Save
            </Button>
            <Button
              variant="contained"
              sx={greyBtn}
              onClick={() => original && setForm(original)}
            >
              Reset
            </Button>
          </Stack>
        </Paper>
      </Stack>
      {ToastEl}
    </Box>
  );
}
